/**
 * VerPecaPage — leitura de uma impugnação ou defesa gerada (F7).
 *
 * Réplica dos view_impugnacao.html / view_defesa.html: cabeçalho, ações,
 * os itens que embasam a peça e o corpo do documento.
 */

import {
  faArrowLeft,
  faBalanceScale,
  faCalendar,
  faExclamationTriangle,
  faFileWord,
  faGavel,
  faShieldAlt,
  faSpinner,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { documentoPeca, excluirPeca, type TipoPeca } from '../../api/juridico'
import { useToast } from '../../components/Toast'

export function VerPecaPage() {
  const { tipo, id } = useParams()
  const atual: TipoPeca = tipo === 'defesa' ? 'defesa' : 'impugnacao'
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [confirmando, setConfirmando] = useState(false)

  const { data: peca, isLoading, error } = useQuery({
    queryKey: ['juridico-peca', atual, idNumerico],
    queryFn: () => documentoPeca(atual, idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  const excluir = useMutation({
    mutationFn: () => excluirPeca(atual, idNumerico),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['juridico-pecas', atual] })
      notificar('success', 'Documento excluído.')
      navegar(`/juridico/${atual}`)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando documento…
      </p>
    )
  }
  if (error || !peca) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faBalanceScale} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Documento não encontrado
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'O documento não existe ou não é seu.'}
        </p>
        <Link
          to={`/juridico/${atual}`}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  const exportar =
    atual === 'impugnacao'
      ? `/juridico/impugnacao/${peca.id}/export/docx`
      : `/juridico/defesa/${peca.id}/export/docx`

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon
            icon={atual === 'impugnacao' ? faGavel : faShieldAlt}
            className="mr-3 text-indigo-600"
          />
          {peca.rotulo}
        </h1>
        <p className="mb-4 text-lg text-slate-600 dark:text-slate-400">{peca.titulo}</p>
        <div className="flex flex-wrap justify-center gap-3">
          {peca.criado_em && (
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
              <FontAwesomeIcon icon={faCalendar} className="mr-2" />
              {peca.criado_em}
            </span>
          )}
          {peca.audit_id && (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              Auditoria #{peca.audit_id}
            </span>
          )}
        </div>
      </header>

      <div className="mb-6 flex flex-col justify-center gap-3 sm:flex-row">
        <a
          href={exportar}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-emerald-700"
        >
          <FontAwesomeIcon icon={faFileWord} className="mr-2" />
          Exportar DOCX
        </a>
        <button
          type="button"
          onClick={() => setConfirmando(true)}
          className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-red-700"
        >
          <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
          Excluir
        </button>
        <Link
          to={`/juridico/${atual}`}
          className="inline-flex items-center justify-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>

      {peca.erro && (
        <div className="mb-6 rounded-r-xl border-l-4 border-red-400 bg-red-50 p-4 dark:border-red-600 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-300">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {peca.erro}
          </p>
        </div>
      )}

      {peca.itens.length > 0 && (
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
            {atual === 'impugnacao'
              ? 'Apontamentos que embasam a peça'
              : 'Argumentos rebatidos'}
          </h2>
          <ul className="space-y-3">
            {peca.itens.map((item, i) => (
              <li
                key={i}
                className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"
              >
                <p className="font-medium text-slate-800 dark:text-slate-100">
                  {item.titulo}
                </p>
                {item.descricao && (
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    {item.descricao}
                  </p>
                )}
                {item.gravidade && (
                  <span className="mt-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    {item.gravidade}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
          <h2 className="flex items-center text-xl font-bold text-white">
            <FontAwesomeIcon icon={faBalanceScale} className="mr-3" />
            {peca.titulo}
          </h2>
        </div>
        <div className="p-8">
          {peca.html ? (
            <div
              className="conteudo-documento leading-relaxed text-slate-700 dark:text-slate-300"
              // HTML gerado pelo agente jurídico, mesma confiança da tela Jinja
              dangerouslySetInnerHTML={{ __html: peca.html }}
            />
          ) : (
            <p className="py-8 text-center text-slate-500">
              O documento ainda não tem conteúdo.
            </p>
          )}
        </div>
      </article>

      {peca.fundamentacao.length > 0 && (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <h2 className="mb-3 text-lg font-semibold text-slate-800 dark:text-slate-100">
            Fundamentação consultada
          </h2>
          <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {peca.fundamentacao.map((f, i) => (
              <li key={i}>• {f}</li>
            ))}
          </ul>
        </section>
      )}

      {confirmando && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={`Excluir ${peca.rotulo}`}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirmando(false)
          }}
        >
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl dark:bg-dark-card">
            <h3 className="mb-2 text-xl font-bold text-slate-800 dark:text-slate-100">
              Excluir {peca.rotulo.toLowerCase()}?
            </h3>
            <p className="mb-6 text-slate-600 dark:text-slate-400">
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className="rounded-xl bg-slate-200 px-6 py-3 font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => excluir.mutate()}
                disabled={excluir.isPending}
                className="rounded-xl bg-red-600 px-6 py-3 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                <FontAwesomeIcon
                  icon={excluir.isPending ? faSpinner : faTrashAlt}
                  spin={excluir.isPending}
                  className="mr-2"
                />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
